import type { Bbox3 } from "../geo/bbox";

/**
 * Persistent, best-effort cache for the byte ranges and derived facts the mesh/zip pipeline
 * pulls out of remote archives.
 *
 * Streaming a multi-gigabyte archive by byte range (see `rangeSource.ts`, `zipReader.ts`)
 * makes a *first* open practical, but on its own buys nothing for a second one: reopening the
 * same file — a page reload, coming back next session — re-runs every ranged request from
 * scratch, including the central directory read and the scan that locates each tile before it
 * can be drawn. This module gives those reads somewhere to land so a repeat open of the same
 * file version is served from disk instead of the network.
 *
 * Keyed by a stable identity supplied by the caller (Trimble Connect file id + version, or a
 * direct URL) rather than by the pre-signed download URL, which changes every time it is
 * re-signed and would otherwise defeat caching entirely.
 *
 * Every operation degrades to a cache miss on failure — no IndexedDB (SSR, disabled storage),
 * a blocked/failed open, quota errors — so a broken cache never breaks loading.
 */

const DB_NAME = "mc3d-range-cache";
const DB_VERSION = 1;
const RANGES_STORE = "ranges";
const SIZES_STORE = "sizes";
const BOUNDS_STORE = "bounds";

/**
 * Soft cap on total cached range bytes, evicted least-recently-used first. Sized to
 * comfortably hold one big archive (the reference dataset is 1.8 GB) without turning into a
 * quota problem for the origin.
 */
const DEFAULT_MAX_BYTES = 2 * 1024 * 1024 * 1024;

/** Stable identity for something cacheable. */
export interface CacheId {
  /** Identity stable across versions, e.g. `connect:<fileId>` — used to drop stale versions. */
  group: string;
  /** Full identity including version, e.g. `connect:<fileId>:<versionId>` — the cache key. */
  key: string;
}

interface RangeRecord {
  id: string;
  group: string;
  key: string;
  offset: number;
  length: number;
  bytes: Uint8Array;
  size: number;
  lastAccess: number;
}

interface BoundsRecord {
  id: string;
  group: string;
  bbox: Bbox3;
  approximate: boolean;
}

function rangeId(key: string, offset: number, length: number): string {
  return `${key} ${offset} ${length}`;
}

function boundsId(key: string, path: string): string {
  return `${key} ${path}`;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

class RangeCache {
  private dbPromise: Promise<IDBDatabase | null> | null = null;
  private totalBytes = -1;
  /** Serialises writes/eviction so concurrent tile loads don't race the running total. */
  private queue: Promise<void> = Promise.resolve();
  private readonly maxBytes = DEFAULT_MAX_BYTES;

  private db(): Promise<IDBDatabase | null> {
    if (!this.dbPromise) this.dbPromise = this.open();
    return this.dbPromise;
  }

  private async open(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === "undefined") return null;
    try {
      return await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          const ranges = db.createObjectStore(RANGES_STORE, { keyPath: "id" });
          ranges.createIndex("by_group", "group");
          ranges.createIndex("by_lastAccess", "lastAccess");
          db.createObjectStore(SIZES_STORE, { keyPath: "key" });
          const bounds = db.createObjectStore(BOUNDS_STORE, { keyPath: "id" });
          bounds.createIndex("by_group", "group");
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error("mc3d-range-cache: blocked by another tab"));
      });
    } catch {
      return null;
    }
  }

  /** Runs `fn` with mutations serialised; failures never propagate to the caller. */
  private enqueue(fn: () => Promise<void>): void {
    this.queue = this.queue.then(fn, fn).catch((error) => {
      console.warn("[rangeCache]", error);
    });
  }

  // ------------------------------------------------------------------- ranges

  async getRange(key: string, offset: number, length: number): Promise<Uint8Array | undefined> {
    const db = await this.db();
    if (!db) return undefined;
    try {
      const tx = db.transaction(RANGES_STORE, "readonly");
      const record = await promisify<RangeRecord | undefined>(
        tx.objectStore(RANGES_STORE).get(rangeId(key, offset, length)),
      );
      if (!record) return undefined;
      // Best-effort recency bump; lost updates under concurrency are fine, this only feeds
      // LRU eviction order.
      this.enqueue(async () => {
        const db2 = await this.db();
        if (!db2) return;
        const writeTx = db2.transaction(RANGES_STORE, "readwrite");
        const store = writeTx.objectStore(RANGES_STORE);
        const current = await promisify<RangeRecord | undefined>(store.get(record.id));
        if (current) {
          current.lastAccess = Date.now();
          store.put(current);
        }
      });
      return record.bytes;
    } catch {
      return undefined;
    }
  }

  putRange(key: string, group: string, offset: number, length: number, bytes: Uint8Array): void {
    if (bytes.byteLength === 0 || bytes.byteLength > this.maxBytes) return;
    this.enqueue(async () => {
      const db = await this.db();
      if (!db) return;
      await this.ensureTotal(db);

      const id = rangeId(key, offset, length);
      const tx = db.transaction(RANGES_STORE, "readwrite");
      const store = tx.objectStore(RANGES_STORE);
      const existing = await promisify<RangeRecord | undefined>(store.get(id));
      if (existing) {
        this.totalBytes -= existing.size;
      }

      await this.evictUntilFits(store, bytes.byteLength);

      const record: RangeRecord = { id, group, key, offset, length, bytes, size: bytes.byteLength, lastAccess: Date.now() };
      store.put(record);
      this.totalBytes += bytes.byteLength;
    });
  }

  /** Sums stored range sizes once per session; cheap relative to the network it replaces. */
  private async ensureTotal(db: IDBDatabase): Promise<void> {
    if (this.totalBytes >= 0) return;
    const tx = db.transaction(RANGES_STORE, "readonly");
    let total = 0;
    await new Promise<void>((resolve, reject) => {
      const request = tx.objectStore(RANGES_STORE).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve();
        total += (cursor.value as RangeRecord).size;
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
    this.totalBytes = total;
  }

  /** Evicts the least-recently-used ranges (across all cached files) until `incoming` fits. */
  private async evictUntilFits(store: IDBObjectStore, incoming: number): Promise<void> {
    if (this.totalBytes + incoming <= this.maxBytes) return;
    const index = store.index("by_lastAccess");
    await new Promise<void>((resolve, reject) => {
      const request = index.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || this.totalBytes + incoming <= this.maxBytes) return resolve();
        const record = cursor.value as RangeRecord;
        this.totalBytes -= record.size;
        cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /** Drops ranges left over from a different (typically older) version of the same file. */
  pruneGroupExcept(group: string, keepKey: string): void {
    this.enqueue(async () => {
      const db = await this.db();
      if (!db) return;
      await this.ensureTotal(db);
      const tx = db.transaction([RANGES_STORE, BOUNDS_STORE], "readwrite");
      await Promise.all([
        prunePlain(tx.objectStore(RANGES_STORE), group, keepKey, (record) => {
          this.totalBytes -= (record as RangeRecord).size;
        }),
        prunePlain(tx.objectStore(BOUNDS_STORE), group, keepKey, () => undefined),
      ]);
    });
  }

  // -------------------------------------------------------------------- sizes

  async getSize(key: string): Promise<number | undefined> {
    const db = await this.db();
    if (!db) return undefined;
    try {
      const tx = db.transaction(SIZES_STORE, "readonly");
      const record = await promisify<{ key: string; size: number } | undefined>(tx.objectStore(SIZES_STORE).get(key));
      return record?.size;
    } catch {
      return undefined;
    }
  }

  putSize(key: string, size: number): void {
    this.enqueue(async () => {
      const db = await this.db();
      if (!db) return;
      const tx = db.transaction(SIZES_STORE, "readwrite");
      tx.objectStore(SIZES_STORE).put({ key, size });
    });
  }

  // ------------------------------------------------------------------- bounds

  /** Cached tile extent, in the OBJ's local coordinates, from a previous locate pass. */
  async getBounds(key: string, path: string): Promise<{ bbox: Bbox3; approximate: boolean } | undefined> {
    const db = await this.db();
    if (!db) return undefined;
    try {
      const tx = db.transaction(BOUNDS_STORE, "readonly");
      const record = await promisify<BoundsRecord | undefined>(tx.objectStore(BOUNDS_STORE).get(boundsId(key, path)));
      return record ? { bbox: record.bbox, approximate: record.approximate } : undefined;
    } catch {
      return undefined;
    }
  }

  putBounds(key: string, group: string, path: string, bbox: Bbox3, approximate: boolean): void {
    this.enqueue(async () => {
      const db = await this.db();
      if (!db) return;
      const tx = db.transaction(BOUNDS_STORE, "readwrite");
      const id = boundsId(key, path);
      const store = tx.objectStore(BOUNDS_STORE);
      // An exact result replaces an approximate one; an approximate result never
      // downgrades one already known to be exact.
      const existing = await promisify<BoundsRecord | undefined>(store.get(id));
      if (existing && !existing.approximate && approximate) return;
      const record: BoundsRecord = { id, group, bbox, approximate };
      store.put(record);
    });
  }
}

function prunePlain(
  store: IDBObjectStore,
  group: string,
  keepKey: string,
  onDelete: (record: unknown) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = store.index("by_group").openCursor(IDBKeyRange.only(group));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return resolve();
      const record = cursor.value as { key?: string; id: string };
      // Bounds records have no `key` field of their own; their id is prefixed with it.
      const belongsToOtherVersion = record.key ? record.key !== keepKey : !record.id.startsWith(`${keepKey} `);
      if (belongsToOtherVersion) {
        onDelete(record);
        cursor.delete();
      }
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });
}

export const rangeCache = new RangeCache();
